import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageSquare, Phone, User, Bot, Shield, RefreshCw, Send, AlertCircle,
  CheckCircle, ChevronLeft, Search, Archive, ArchiveRestore, Trash2, X, Clock, History
} from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { supabase } from '../../lib/supabase';

// ─── Phone normalization ────────────────────────────────────────────────────
function cleanPhone(phone) {
  return String(phone || '')?.replace(/\D/g, '');
}

function displayPhone(phone) {
  if (!phone) return '';
  const digits = cleanPhone(phone);
  if (digits?.startsWith('33') && digits?.length === 11) {
    return '+33 ' + digits?.substring(2)?.replace(/(\d)(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
  }
  return '+' + digits;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return "À l'instant";
  if (mins < 60) return `Il y a ${mins} min`;
  if (hours < 24) return `Il y a ${hours}h`;
  if (days < 7) return `Il y a ${days}j`;
  return d?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr)?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function formatFullDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr)?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ─── Normalize text for search (remove accents) ─────────────────────────────
function normalizeText(str) {
  return String(str || '')?.toLowerCase()?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, '');
}

// ─── Determine conversation open/archived status ─────────────────────────────
// A conversation is "open" if:
//   - it has never been archived (no row with archived=true)
//   OR
//   - it has a message with created_at > archived_at (client returned)
function getConversationStatus(rows) {
  const archivedRows = rows?.filter(r => r?.archived === true);
  if (archivedRows?.length === 0) return 'open';

  // Find the latest archived_at
  const latestArchivedAt = archivedRows?.map(r => r?.archived_at)?.filter(Boolean)?.sort()?.pop();

  if (!latestArchivedAt) return 'archived';

  // Check if any message is more recent than archived_at
  const hasNewerMessage = rows?.some(r => {
    return r?.created_at && new Date(r.created_at) > new Date(latestArchivedAt);
  });

  if (hasNewerMessage) return 'reopened'; // client returned
  return 'archived';
}

// ─── Toast component ─────────────────────────────────────────────────────────
function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  const colors = {
    success: 'bg-green-600',
    error: 'bg-red-600',
    info: 'bg-blue-600',
  };

  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white text-sm font-medium ${colors?.[type] || colors?.success} animate-fade-in`}>
      {type === 'success' && <CheckCircle className="h-4 w-4 flex-shrink-0" />}
      {type === 'error' && <AlertCircle className="h-4 w-4 flex-shrink-0" />}
      {type === 'info' && <Clock className="h-4 w-4 flex-shrink-0" />}
      <span>{message}</span>
      <button onClick={onClose} className="ml-1 opacity-70 hover:opacity-100">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ─── Confirmation dialog ─────────────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <Trash2 className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Confirmation requise</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{message}</p>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
          >
            Supprimer définitivement
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Message bubble component ────────────────────────────────────────────────
function MessageBubble({ msg }) {
  const bubbles = [];

  if (msg?.user_message) {
    bubbles?.push(
      <div key="user" className="flex justify-start mb-2">
        <div className="max-w-[75%]">
          <div className="flex items-center gap-1 mb-1">
            <User className="h-3 w-3 text-gray-500" />
            <span className="text-xs text-gray-500">Client</span>
          </div>
          <div className="bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-2xl rounded-tl-sm px-4 py-2.5 shadow-sm">
            <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{msg?.user_message}</p>
          </div>
          <p className="text-xs text-gray-400 mt-1 ml-1">{formatTime(msg?.created_at)}</p>
        </div>
      </div>
    );
  }

  if (msg?.assistant_response) {
    bubbles?.push(
      <div key="assistant" className="flex justify-end mb-2">
        <div className="max-w-[75%]">
          <div className="flex items-center gap-1 mb-1 justify-end">
            <span className="text-xs text-emerald-600">Fatou IA</span>
            <Bot className="h-3 w-3 text-emerald-600" />
          </div>
          <div className="bg-emerald-500 rounded-2xl rounded-tr-sm px-4 py-2.5 shadow-sm">
            <p className="text-sm text-white whitespace-pre-wrap">{msg?.assistant_response}</p>
          </div>
          <p className="text-xs text-gray-400 mt-1 text-right mr-1">{formatTime(msg?.created_at)}</p>
        </div>
      </div>
    );
  }

  if (msg?.admin_message) {
    bubbles?.push(
      <div key="admin" className="flex justify-end mb-2">
        <div className="max-w-[75%]">
          <div className="flex items-center gap-1 mb-1 justify-end">
            <span className="text-xs text-blue-600">Admin</span>
            <Shield className="h-3 w-3 text-blue-600" />
          </div>
          <div className="bg-blue-500 rounded-2xl rounded-tr-sm px-4 py-2.5 shadow-sm">
            <p className="text-sm text-white whitespace-pre-wrap">{msg?.admin_message}</p>
          </div>
          <p className="text-xs text-gray-400 mt-1 text-right mr-1">{formatTime(msg?.created_at)}</p>
        </div>
      </div>
    );
  }

  if (bubbles?.length === 0) return null;
  return <>{bubbles}</>;
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function AdminWhatsApp() {
  const [allMessages, setAllMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [selectedConvMeta, setSelectedConvMeta] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState('');
  const [takingOver, setTakingOver] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('open'); // 'open' | 'archived' | 'all'
  const [mobileShowMessages, setMobileShowMessages] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [toast, setToast] = useState(null); // { message, type }
  const [confirmDelete, setConfirmDelete] = useState(null); // phone to delete
  const [actionLoading, setActionLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  // ── Build conversations from raw messages ────────────────────────────────
  const buildConversations = useCallback((data) => {
    const grouped = {};
    for (const row of (data || [])) {
      const phone = String(row?.phone || '')?.replace(/\D/g, '');
      if (!phone) continue;
      if (!grouped?.[phone]) {
        grouped[phone] = { phone, rows: [] };
      }
      grouped?.[phone]?.rows?.push(row);
    }

    const convList = Object.values(grouped)?.map(g => {
      // Sort rows by created_at DESC to get latest first
      const sortedRows = [...g?.rows]?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const latest = sortedRows?.[0];
      const lastMsg = latest?.user_message || latest?.assistant_response || latest?.admin_message || 'Message vide';

      const convStatus = getConversationStatus(g?.rows);

      // Find previous archived_at for "returning client" info
      const archivedRows = g?.rows?.filter(r => r?.archived === true && r?.archived_at);
      const lastArchivedAt = archivedRows?.length > 0
        ? archivedRows?.sort((a, b) => new Date(b.archived_at) - new Date(a.archived_at))?.[0]?.archived_at
        : null;

      return {
        phone: g?.phone,
        last_message: lastMsg,
        last_message_at: latest?.created_at,
        status: convStatus, // 'open' | 'archived' | 'reopened'
        lastArchivedAt,
        messages: g?.rows,
        human_takeover: false,
      };
    });

    // Sort by last_message_at DESC
    convList?.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
    return convList;
  }, []);

  // ── Load 1000 last messages from wa_messages ─────────────────────────────
  const loadAllMessages = useCallback(async () => {
    setLoadingConvs(true);
    setLoadError(null);
    try {
      const { data, error } = await supabase?.from('wa_messages')?.select('id, phone, user_message, assistant_response, admin_message, created_at, order_confirmed, archived, archived_at, archived_by')?.order('created_at', { ascending: false })?.limit(1000);

      if (error) {
        console.error('WA_MESSAGES_ERROR', error);
        setLoadError(error?.message || 'Erreur lors du chargement des messages');
        setLoadingConvs(false);
        return;
      }

      console.log('WA_MESSAGES_LOADED', data?.length, data);

      const convList = buildConversations(data);

      // Enrich with wa_conversations (human_takeover) — optional
      if (convList?.length > 0) {
        const phones = convList?.map(c => c?.phone);
        const { data: convData } = await supabase?.from('wa_conversations')?.select('phone, human_takeover, status')?.in('phone', phones);

        if (convData && convData?.length > 0) {
          const convMap = {};
          for (const c of convData) {
            convMap[String(c.phone || '').replace(/\D/g, '')] = c;
          }
          for (const g of convList) {
            const match = convMap?.[g?.phone];
            if (match) {
              g.human_takeover = match?.human_takeover ?? false;
            }
          }
        }
      }

      console.log('CONVERSATIONS_BUILT', convList);
      setAllMessages(data || []);
      setConversations(convList);
    } catch (err) {
      console.error('WA_MESSAGES_ERROR', err);
      setLoadError(err?.message || 'Erreur inattendue');
    } finally {
      setLoadingConvs(false);
    }
  }, [buildConversations]);

  useEffect(() => {
    loadAllMessages();
  }, [loadAllMessages]);

  // ── Real-time subscription ───────────────────────────────────────────────
  useEffect(() => {
    const channel = supabase?.channel('wa_messages_realtime')?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'wa_messages' }, (payload) => {
        const newMsg = payload?.new;
        setAllMessages(prev => {
          const updated = [newMsg, ...prev];
          setConversations(buildConversations(updated));
          return updated;
        });
      })?.subscribe();

    return () => { supabase?.removeChannel(channel); };
  }, [buildConversations]);

  // ── Auto-scroll ──────────────────────────────────────────────────────────
  const selectedMessages = selectedPhone
    ? [...allMessages?.filter(row => String(row?.phone || '')?.replace(/\D/g, '') === selectedPhone)]?.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    : [];

  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedMessages?.length]);

  // ── Advanced search ──────────────────────────────────────────────────────
  const filteredConversations = conversations?.filter(conv => {
    // Status filter
    if (statusFilter === 'open' && conv?.status !== 'open' && conv?.status !== 'reopened') return false;
    if (statusFilter === 'archived' && conv?.status !== 'archived') return false;

    // Search filter
    if (!searchQuery) return true;

    const search = normalizeText(searchQuery);
    const cleanSearchPhone = searchQuery?.replace(/\D/g, '');

    return conv?.messages?.some(row => {
      const phone = String(row?.phone || '');
      const cleanRowPhone = phone?.replace(/\D/g, '');

      const text = [
        row?.phone,
        row?.user_message,
        row?.assistant_response,
        row?.admin_message,
        row?.created_at,
      ]?.filter(Boolean)?.join(' ');

      const normalizedText = normalizeText(text);

      return (normalizedText?.includes(search) || (cleanSearchPhone && cleanRowPhone?.includes(cleanSearchPhone)));
    });
  });

  // ── Archive conversation ─────────────────────────────────────────────────
  const handleArchive = async (phone) => {
    if (!phone) return;
    setActionLoading(true);
    try {
      const { error } = await supabase?.from('wa_messages')?.update({
          archived: true,
          archived_at: new Date()?.toISOString(),
          archived_by: 'admin',
        })?.eq('phone', phone);

      if (error) throw error;

      // If this was the selected conversation, deselect
      if (selectedPhone === phone) {
        setSelectedPhone(null);
        setSelectedConvMeta(null);
        setMobileShowMessages(false);
      }

      await loadAllMessages();
      showToast('Conversation archivée', 'success');
    } catch (err) {
      console.error('Archive error:', err);
      showToast('Erreur lors de l\'archivage', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Restore conversation ─────────────────────────────────────────────────
  const handleRestore = async (phone) => {
    if (!phone) return;
    setActionLoading(true);
    try {
      const { error } = await supabase?.from('wa_messages')?.update({
          archived: false,
          archived_at: null,
          archived_by: null,
        })?.eq('phone', phone);

      if (error) throw error;

      await loadAllMessages();
      showToast('Conversation restaurée', 'success');
    } catch (err) {
      console.error('Restore error:', err);
      showToast('Erreur lors de la restauration', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Delete conversation ──────────────────────────────────────────────────
  const handleDeleteConfirmed = async () => {
    const rawPhone = confirmDelete;
    if (!rawPhone) return;
    setConfirmDelete(null);
    setActionLoading(true);

    // Clean the phone number to digits only
    const cleanPhoneNum = String(rawPhone || '')?.replace(/\D/g, '');

    console.log('DELETE_SELECTED_CONVERSATION', selectedConvMeta);
    console.log('DELETE_CLEAN_PHONE', cleanPhoneNum);

    try {
      // Step 1: Find all rows for this phone with robust search (exact + ilike fallback)
      const { data: rowsToDelete, error: selectError } = await supabase?.from('wa_messages')?.select('id, phone')?.or(`phone.eq.${cleanPhoneNum},phone.ilike.%${cleanPhoneNum}%`);

      if (selectError) {
        console.error('DELETE_SELECT_ERROR', selectError);
        showToast('Erreur recherche messages : ' + selectError?.message, 'error');
        setActionLoading(false);
        return;
      }

      console.log('DELETE_ROWS_FOUND', rowsToDelete);

      if (!rowsToDelete || rowsToDelete?.length === 0) {
        console.warn('NO_ROWS_TO_DELETE', cleanPhoneNum);
        showToast('Aucun message trouvé pour ce numéro', 'error');
        setActionLoading(false);
        return;
      }

      const ids = rowsToDelete?.map((row) => row?.id);
      console.log('DELETE_IDS', ids);

      // Step 2: Delete by id (not by phone) for reliability
      const { error: deleteError } = await supabase?.from('wa_messages')?.delete()?.in('id', ids);

      if (deleteError) {
        console.error('DELETE_ERROR', deleteError);
        showToast('Erreur suppression : ' + deleteError?.message, 'error');
        setActionLoading(false);
        return;
      }

      // Step 3: Immediately update local state
      setAllMessages((prev) => prev?.filter((row) => !ids?.includes(row?.id)));
      setConversations((prev) => prev?.filter((conv) => conv?.phone !== cleanPhoneNum));

      if (selectedPhone === cleanPhoneNum) {
        setSelectedPhone(null);
        setSelectedConvMeta(null);
        setMobileShowMessages(false);
      }

      // Step 4: Reload from Supabase to confirm deletion
      await loadAllMessages();

      showToast('Conversation supprimée définitivement', 'success');
    } catch (err) {
      console.error('Delete error:', err);
      showToast('Erreur lors de la suppression', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Send admin reply ─────────────────────────────────────────────────────
  const handleSendReply = async () => {
    if (!replyText?.trim() || !selectedPhone) return;
    setSending(true);
    setSendError('');
    setSendSuccess('');

    const messageText = replyText?.trim();

    try {
      const { data: fnData, error: fnError } = await supabase?.functions?.invoke('send-whatsapp-admin-message', {
        body: { to: selectedPhone, message: messageText }
      });

      if (fnError) {
        setSendError("Erreur lors de l'envoi du message WhatsApp.");
        setSending(false);
        return;
      }

      if (!fnData?.success) {
        setSendError(`Erreur Meta API: ${fnData?.error || 'Envoi échoué'}`);
        setSending(false);
        return;
      }

      const { data: insertedMsg, error: insertError } = await supabase?.from('wa_messages')?.insert({ phone: selectedPhone, admin_message: messageText, created_at: new Date()?.toISOString() })?.select()?.single();

      if (!insertError && insertedMsg) {
        setAllMessages(prev => {
          const updated = [insertedMsg, ...prev];
          setConversations(buildConversations(updated));
          return updated;
        });
      }

      await supabase?.from('wa_conversations')?.update({ updated_at: new Date()?.toISOString() })?.eq('phone', selectedPhone);

      setReplyText('');
      setSendSuccess('Message envoyé avec succès !');
      setTimeout(() => setSendSuccess(''), 3000);
    } catch (err) {
      setSendError('Erreur inattendue lors de l\'envoi.');
    } finally {
      setSending(false);
    }
  };

  // ── Human takeover toggle ────────────────────────────────────────────────
  const handleHumanTakeover = async (value) => {
    if (!selectedPhone) return;
    setTakingOver(true);
    try {
      const { error } = await supabase?.from('wa_conversations')?.upsert(
        { phone: selectedPhone, human_takeover: value, updated_at: new Date()?.toISOString() },
        { onConflict: 'phone' }
      );
      if (error) throw error;
      setSelectedConvMeta(prev => ({ ...prev, human_takeover: value }));
      setConversations(prev => prev?.map(c => c?.phone === selectedPhone ? { ...c, human_takeover: value } : c));
    } catch (err) {
      console.error('Takeover error:', err);
    } finally {
      setTakingOver(false);
    }
  };

  const handleSelectConv = (conv) => {
    setSelectedPhone(conv?.phone);
    setSelectedConvMeta(conv);
    setMobileShowMessages(true);
    setSendError('');
    setSendSuccess('');
    setReplyText('');
  };

  const currentConv = conversations?.find(c => c?.phone === selectedPhone) || selectedConvMeta;

  // Count for filter badges
  const openCount = conversations?.filter(c => c?.status === 'open' || c?.status === 'reopened')?.length;
  const archivedCount = conversations?.filter(c => c?.status === 'archived')?.length;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <AdminNavigation />
      {/* Toast */}
      {toast && (
        <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />
      )}
      {/* Confirm delete dialog */}
      {confirmDelete && (
        <ConfirmDialog
          message="Voulez-vous vraiment supprimer définitivement cette conversation ? Cette action supprimera tous les messages de ce numéro et ne pourra pas être annulée."
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
      <div className="flex h-[calc(100vh-64px)]">
        {/* ── Left panel ─────────────────────────────────────────────────── */}
        <div className={`${mobileShowMessages ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 lg:w-96 bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700`}>
          {/* Header */}
          <div className="p-4 border-b border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-green-600" />
                <h1 className="text-lg font-bold text-gray-900 dark:text-white">WhatsApp Admin</h1>
              </div>
              <button
                onClick={loadAllMessages}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-gray-400 transition-colors"
                title="Actualiser"
              >
                <RefreshCw className={`h-4 w-4 ${loadingConvs ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Advanced search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Recherche : nom, ville, code postal, numéro, message..."
                value={searchQuery}
                onChange={e => setSearchQuery(e?.target?.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-gray-100 dark:bg-slate-700 border-0 rounded-lg text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Status filters */}
            <div className="flex gap-1">
              {[
                { key: 'open', label: 'Ouvertes', count: openCount },
                { key: 'archived', label: 'Archivées', count: archivedCount },
                { key: 'all', label: 'Toutes', count: conversations?.length },
              ]?.map(f => (
                <button
                  key={f?.key}
                  onClick={() => setStatusFilter(f?.key)}
                  className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded-lg text-xs font-medium transition-colors ${
                    statusFilter === f?.key
                      ? 'bg-green-600 text-white' :'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {f?.label}
                  <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold ${
                    statusFilter === f?.key ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-gray-300'
                  }`}>
                    {f?.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Error display */}
          {loadError && (
            <div className="mx-3 mt-3 flex items-start gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-red-600 dark:text-red-400">Erreur de chargement</p>
                <p className="text-xs text-red-500 dark:text-red-400 mt-0.5">{loadError}</p>
              </div>
            </div>
          )}

          {/* Conversations list */}
          <div className="flex-1 overflow-y-auto">
            {loadingConvs ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
              </div>
            ) : filteredConversations?.length === 0 && !loadError ? (
              <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                <MessageSquare className="h-8 w-8 mb-2 opacity-40" />
                <p className="text-sm">
                  {searchQuery ? 'Aucun résultat pour cette recherche' : 'Aucune conversation'}
                </p>
              </div>
            ) : (
              filteredConversations?.map(conv => (
                <div key={conv?.phone} className="relative group">
                  <button
                    onClick={() => handleSelectConv(conv)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors ${
                      selectedPhone === conv?.phone ? 'bg-green-50 dark:bg-green-900/20 border-l-4 border-l-green-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                          conv?.status === 'archived' ? 'bg-gray-400' :
                          conv?.status === 'reopened'? 'bg-orange-500' : conv?.human_takeover ?'bg-blue-500' : 'bg-green-500'
                        }`}>
                          {conv?.status === 'archived' ? <Archive className="h-4 w-4" /> :
                           conv?.status === 'reopened' ? <History className="h-4 w-4" /> :
                           conv?.human_takeover ? <Shield className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {displayPhone(conv?.phone)}
                          </p>
                          {conv?.last_message && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{conv?.last_message}</p>
                          )}
                          {/* Badges */}
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {conv?.status === 'reopened' && (
                              <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 rounded">
                                Conversation réouverte
                              </span>
                            )}
                            {conv?.status === 'archived' && (
                              <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded">
                                Archivée
                              </span>
                            )}
                            {conv?.human_takeover && conv?.status !== 'archived' && (
                              <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 rounded">
                                Admin
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <p className="text-xs text-gray-400">{formatDate(conv?.last_message_at)}</p>
                      </div>
                    </div>
                  </button>

                  {/* Action buttons on hover */}
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-1 bg-white dark:bg-slate-800 shadow-md rounded-lg p-1 z-10">
                    {(conv?.status === 'open' || conv?.status === 'reopened') && (
                      <button
                        onClick={(e) => { e?.stopPropagation(); handleArchive(conv?.phone); }}
                        disabled={actionLoading}
                        title="Archiver"
                        className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 hover:text-orange-600 transition-colors disabled:opacity-50"
                      >
                        <Archive className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {conv?.status === 'archived' && (
                      <button
                        onClick={(e) => { e?.stopPropagation(); handleRestore(conv?.phone); }}
                        disabled={actionLoading}
                        title="Restaurer"
                        className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 hover:text-green-600 transition-colors disabled:opacity-50"
                      >
                        <ArchiveRestore className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      onClick={(e) => { e?.stopPropagation(); setConfirmDelete(conv?.phone); }}
                      disabled={actionLoading}
                      title="Supprimer définitivement"
                      className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 hover:text-red-600 transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── Right panel: Messages ───────────────────────────────────────── */}
        <div className={`${mobileShowMessages ? 'flex' : 'hidden md:flex'} flex-col flex-1 bg-gray-50 dark:bg-slate-900`}>
          {!selectedPhone ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <MessageSquare className="h-16 w-16 mb-4 opacity-20" />
              <p className="text-lg font-medium">Sélectionnez une conversation</p>
              <p className="text-sm mt-1">Cliquez sur un contact à gauche pour voir ses messages</p>
            </div>
          ) : (
            <>
              {/* Conversation header */}
              <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setMobileShowMessages(false)}
                      className="md:hidden p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
                    >
                      <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                    </button>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white ${
                      currentConv?.status === 'archived' ? 'bg-gray-400' :
                      currentConv?.status === 'reopened'? 'bg-orange-500' : currentConv?.human_takeover ?'bg-blue-500' : 'bg-green-500'
                    }`}>
                      <Phone className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">{displayPhone(selectedPhone)}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {selectedMessages?.length} message{selectedMessages?.length !== 1 ? 's' : ''} • Dernier : {formatFullDate(currentConv?.last_message_at)}
                      </p>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    {/* Archive / Restore */}
                    {(currentConv?.status === 'open' || currentConv?.status === 'reopened') && (
                      <button
                        onClick={() => handleArchive(selectedPhone)}
                        disabled={actionLoading}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 rounded-lg hover:bg-orange-200 transition-colors disabled:opacity-50"
                      >
                        <Archive className="h-3.5 w-3.5" />
                        Archiver
                      </button>
                    )}
                    {currentConv?.status === 'archived' && (
                      <button
                        onClick={() => handleRestore(selectedPhone)}
                        disabled={actionLoading}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50"
                      >
                        <ArchiveRestore className="h-3.5 w-3.5" />
                        Restaurer
                      </button>
                    )}
                    {/* Delete */}
                    <button
                      onClick={() => setConfirmDelete(selectedPhone)}
                      disabled={actionLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Supprimer
                    </button>
                    {/* Human takeover */}
                    {currentConv?.human_takeover ? (
                      <button
                        onClick={() => handleHumanTakeover(false)}
                        disabled={takingOver}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-lg hover:bg-emerald-200 transition-colors disabled:opacity-50"
                      >
                        <Bot className="h-3.5 w-3.5" />
                        Réactiver Fatou
                      </button>
                    ) : (
                      <button
                        onClick={() => handleHumanTakeover(true)}
                        disabled={takingOver}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
                      >
                        <Shield className="h-3.5 w-3.5" />
                        Reprise humaine
                      </button>
                    )}
                    <button
                      onClick={loadAllMessages}
                      className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-gray-400 transition-colors"
                      title="Actualiser"
                    >
                      <RefreshCw className={`h-4 w-4 ${loadingConvs ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>

                {/* Returning client banner */}
                {currentConv?.status === 'reopened' && currentConv?.lastArchivedAt && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                    <History className="h-4 w-4 text-orange-600 flex-shrink-0" />
                    <p className="text-xs text-orange-700 dark:text-orange-300">
                      <strong>Historique client retrouvé</strong> — dernier contact précédent : {formatFullDate(currentConv?.lastArchivedAt)}
                    </p>
                  </div>
                )}

                {/* Human takeover banner */}
                {currentConv?.human_takeover && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <Shield className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      <strong>Reprise humaine active</strong> — Fatou IA ne répondra plus automatiquement à ce client.
                    </p>
                  </div>
                )}

                {/* Archived banner */}
                {currentConv?.status === 'archived' && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900/20 rounded-lg border border-gray-200 dark:border-gray-700">
                    <Archive className="h-4 w-4 text-gray-500 flex-shrink-0" />
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      <strong>Conversation archivée</strong> — Restaurez-la pour reprendre les échanges.
                    </p>
                  </div>
                )}
              </div>

              {/* Messages area */}
              <div className="flex-1 overflow-y-auto px-4 py-4">
                {selectedMessages?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                    <MessageSquare className="h-8 w-8 mb-2 opacity-40" />
                    <p className="text-sm">Aucun message trouvé pour ce numéro</p>
                    <p className="text-xs mt-1 text-gray-300">Numéro recherché : {selectedPhone}</p>
                  </div>
                ) : (
                  <>
                    {selectedMessages?.map(msg => (
                      <MessageBubble key={msg?.id} msg={msg} />
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Reply area */}
              <div className="bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 p-4">
                {sendError && (
                  <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                    <p className="text-xs text-red-600 dark:text-red-400">{sendError}</p>
                  </div>
                )}
                {sendSuccess && (
                  <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <p className="text-xs text-green-600 dark:text-green-400">{sendSuccess}</p>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <textarea
                    value={replyText}
                    onChange={e => setReplyText(e?.target?.value)}
                    onKeyDown={e => {
                      if (e?.key === 'Enter' && !e?.shiftKey) {
                        e?.preventDefault();
                        handleSendReply();
                      }
                    }}
                    placeholder="Répondre au client... (Entrée pour envoyer, Maj+Entrée pour nouvelle ligne)"
                    rows={2}
                    className="flex-1 resize-none px-3 py-2.5 text-sm bg-gray-100 dark:bg-slate-700 border-0 rounded-xl text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <button
                    onClick={handleSendReply}
                    disabled={sending || !replyText?.trim()}
                    className="flex-shrink-0 p-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 dark:disabled:bg-slate-600 text-white rounded-xl transition-colors disabled:cursor-not-allowed"
                    title="Envoyer"
                  >
                    {sending ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <Send className="h-5 w-5" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                  Envoi via Meta WhatsApp Cloud API • Numéro ADT : +33651988621
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
